import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { alphaApi, type SearchHit } from '../api/alpha';
import { getRecentPaths } from '../shell/shellPrefs';

const RECENT_SEARCH_KEY = 'founder-os-search-recent';

function getSearchRecent(): string[] {
  try {
    const raw = localStorage.getItem(RECENT_SEARCH_KEY);
    if (!raw) return [];
    const v = JSON.parse(raw) as unknown;
    return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string').slice(0, 8) : [];
  } catch {
    return [];
  }
}

function pushSearchRecent(q: string) {
  const next = [q, ...getSearchRecent().filter((x) => x !== q)].slice(0, 8);
  try {
    localStorage.setItem(RECENT_SEARCH_KEY, JSON.stringify(next));
  } catch {
    /* ignore */
  }
  return next;
}

/**
 * Global search framework — providers via alpha search + local recent.
 * Not business entity search. Future providers plug into searchHits path.
 */
export function GlobalSearch() {
  const [query, setQuery] = useState('');
  const [hits, setHits] = useState<SearchHit[]>([]);
  const [open, setOpen] = useState(false);
  const [recent, setRecent] = useState(() => getSearchRecent());
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === '/' && !(e.target instanceof HTMLInputElement) && !(e.target instanceof HTMLTextAreaElement)) {
        e.preventDefault();
        inputRef.current?.focus();
        setOpen(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    if (query.length < 2) {
      setHits([]);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        const result = await alphaApi.search(query);
        setHits(result.hits);
        setOpen(true);
      } catch {
        // Framework fallback: page recent navigation as soft hints
        const paths = getRecentPaths()
          .filter((p) => p.toLowerCase().includes(query.toLowerCase()))
          .slice(0, 5)
          .map((path, i) => ({
            id: `local-${path}`,
            title: path,
            description: 'Recent navigation',
            module: 'shell',
            path,
            type: 'page',
            score: 1 - i * 0.1,
          }));
        setHits(paths);
        setOpen(paths.length > 0);
      }
    }, 250);
    return () => clearTimeout(timer);
  }, [query]);

  return (
    <div className="relative w-full max-w-xl">
      <label htmlFor="global-search" className="sr-only">
        Global search
      </label>
      <input
        ref={inputRef}
        id="global-search"
        type="search"
        role="searchbox"
        placeholder="Search pages… (/ to focus)"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            setOpen(false);
            (e.target as HTMLInputElement).blur();
          }
          if (e.key === 'Enter' && query.trim().length >= 2) {
            setRecent(pushSearchRecent(query.trim()));
          }
        }}
        className="w-full min-h-[44px] rounded-[var(--shell-radius-md)] border border-[var(--shell-border)] bg-[var(--shell-surface-elevated)] px-4 py-2 text-sm text-[var(--shell-fg)] shadow-sm placeholder:text-[var(--shell-fg-subtle)] focus:border-[var(--shell-accent)] focus:outline-none focus:ring-2 focus:ring-[var(--shell-focus-ring)]"
      />
      {open && (hits.length > 0 || (query.length < 2 && recent.length > 0)) && (
        <ul
          role="listbox"
          aria-label="Search results"
          className="absolute z-50 mt-1 max-h-72 w-full overflow-auto rounded-[var(--shell-radius-md)] border border-[var(--shell-border)] bg-[var(--shell-surface-elevated)] shadow-lg"
        >
          {query.length < 2 &&
            recent.map((r) => (
              <li key={`r-${r}`} role="option">
                <button
                  type="button"
                  className="block w-full px-4 py-3 text-left text-sm text-[var(--shell-fg-muted)] hover:bg-[var(--shell-hover)]"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => setQuery(r)}
                >
                  Recent: {r}
                </button>
              </li>
            ))}
          {hits.map((hit) => (
            <li key={hit.id} role="option">
              <Link
                to={hit.path}
                className="block px-4 py-3 hover:bg-[var(--shell-hover)]"
                onClick={() => {
                  setOpen(false);
                  setRecent(pushSearchRecent(query));
                  setQuery('');
                }}
              >
                <p className="text-sm font-medium text-[var(--shell-fg)]">{hit.title}</p>
                {hit.description && (
                  <p className="text-xs text-[var(--shell-fg-muted)]">{hit.description}</p>
                )}
                <p className="mt-1 text-xs text-[var(--shell-accent)]">{hit.module}</p>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
